'use client';

import { useCallback, useMemo } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { ArrowRightIcon, EyeIcon } from 'lucide-react';
import { getAllProjects, generateLayoutPresets, getAllTranslatedProjects } from '@/data/projects';

type ProjectCopy = {
  id?: string;
  slug?: string;
  title: string;
  category: string;
  summary?: string;
};

type ProjectCard = ProjectCopy & {
  layoutClass: string;
  image: string;
  showSummary: boolean;
  gradientFrom: string;
  gradientTo: string;
};

// Use centralized layout presets
const LAYOUT_PRESETS = generateLayoutPresets();

export default function ProjectsContent() {
  const t = useTranslations('Projects');
  const locale = useLocale();

  const translatedProjects = useMemo(() => {
    const allProjects = getAllProjects();
    return allProjects.map(project => ({
      id: project.id,
      slug: project.id,
      title: project.title,
      category: project.category,
      summary: project.description
    }));
  }, [t]);

  const projects: ProjectCard[] = useMemo(
    () =>
      translatedProjects.map((item, index) => {
        const preset = LAYOUT_PRESETS[index] ?? LAYOUT_PRESETS[LAYOUT_PRESETS.length - 1];
        const slug = item.slug || item.id || `project-${index + 1}`;

        return {
          ...preset,
          ...item,
          slug
        };
      }),
    [translatedProjects]
  );

  const buildProjectHref = useCallback(
    (slug: string) => `/${locale}/projects/${slug}`,
    [locale]
  );

  return (
    <section className="bg-gradient-to-b from-slate-50 to-white py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-r from-brand-secondary/5 to-[#8b5cf6]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-[#8b5cf6]/5 to-[#a855f7]/5 rounded-full blur-3xl"></div>
      </div>

      <div className="mx-auto max-w-7xl relative z-10">
        <header className="mx-auto max-w-4xl text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-secondary/10 backdrop-blur-sm border border-brand-secondary/20 mb-6">
            <EyeIcon className="w-4 h-4 text-brand-secondary" />
            <span className="text-sm font-medium text-brand-secondary tracking-wider">
              {t('tagline')}
            </span>
          </div>
          <p className="text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
            {t('description')}
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-4">
          {projects.map((project, index) => (
            <a
              key={project.slug ?? project.title}
              href={buildProjectHref(project.slug ?? `project-${index + 1}`)}
              className={`${project.layoutClass} group relative overflow-hidden rounded-xl border border-white/60 bg-slate-900/10 shadow-[0_20px_45px_rgba(15,23,42,0.08)] hover:shadow-[0_25px_60px_rgba(15,23,42,0.12)] transition-all duration-500 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:ring-[#3F74EF]/60`}
              style={{
                animationDelay: `${index * 100}ms`,
                animation: 'fadeInUp 0.6s ease-out forwards'
              }}
            >
              <span className="absolute inset-0 bg-slate-900/10 backdrop-blur-[1.5px]" aria-hidden="true" />
              <img
                src={project.image}
                alt={project.title}
                loading={index > 1 ? 'lazy' : 'eager'}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
              />
              <div
                className="absolute inset-0 opacity-80 transition-opacity duration-500 group-hover:opacity-100"
                style={{
                  background: `linear-gradient(180deg, ${project.gradientFrom} 0%, ${project.gradientTo} 60%, rgba(2,6,23,0.85) 100%)`
                }}
                aria-hidden="true"
              />

              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 transform -translate-x-full group-hover:translate-x-full" />

              <div className="relative flex h-full flex-col justify-end gap-3 p-6 sm:p-7">
                <span
                  className="inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-[0_6px_14px_rgba(53,30,224,0.35)] bg-brand-gradient"
                >
                  {project.category}
                </span>
                <h3 className="text-lg font-semibold text-white sm:text-xl">
                  {project.title}
                </h3>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#E3EBFF] transition-colors duration-300 group-hover:text-white">
                  {t('ctaLabel')}
                  <ArrowRightIcon className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}